/**
 * JST (Asia/Tokyo) 基準の暦計算ヘルパー。
 *
 * Cloud Run 等のプロセスタイムゾーンは UTC のため、「月末」「翌月」を扱う処理は JST 基準で
 * 明示的に算出する必要がある。dr-fassy の `isLastDayOfMonth(timezone)` 等、同種の JST 暦計算が
 * リポジトリに散在しているため共有パッケージへ集約する。
 * @graph-stack jst-calendar
 * @graph-domain Engineering
 * @graph-business JST 基準の月末判定・翌月フォルダ名算出などの暦計算ユーティリティを提供する
 * @graph-connects none
 */
/**
 * Asia/Tokyo (JST) での年・月・日を取得する。
 *
 * Node 24 は full ICU を同梱しており Intl の timeZone 変換が使える。month は 1-based で返す。
 * @graph-stack jst-calendar
 * @graph-domain Engineering
 * @graph-business 指定時刻の JST における年月日 (month は 1-based) を算出する
 * @graph-connects none
 */
export function getJstYmd(now) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(now);
    const valueOf = (type) => Number(parts.find((p) => p.type === type)?.value);
    return { year: valueOf('year'), month: valueOf('month'), day: valueOf('day') };
}
/**
 * JST 基準で「翌月」の年月を `YYYYMM` 形式で返す。例: 2026/7/31 → `202608`。
 * @graph-stack jst-calendar
 * @graph-domain Engineering
 * @graph-business JST 基準の翌月年月 (YYYYMM) を算出する
 * @graph-connects getJstYmd [calls] JST 年月日取得
 */
export function nextMonthFolderName(now) {
    const { year, month } = getJstYmd(now);
    const nextYear = month === 12 ? year + 1 : year;
    const nextMonth = month === 12 ? 1 : month + 1;
    return `${String(nextYear)}${String(nextMonth).padStart(2, '0')}`;
}
/**
 * JST 基準で当日がその月の最終日かどうかを返す。
 *
 * `Date.UTC(year, month, 0)` は「`month` 月の 0 日目 = 前月の末日」を指すため、1-based の
 * month をそのまま渡すと当月の日数が得られる (タイムゾーン非依存の純粋な暦計算)。
 * @graph-stack jst-calendar
 * @graph-domain Engineering
 * @graph-business JST 基準で当日が月末かどうかを判定する
 * @graph-connects getJstYmd [calls] JST 年月日取得
 */
export function isLastDayOfMonthJst(now) {
    const { year, month, day } = getJstYmd(now);
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return day === daysInMonth;
}
//# sourceMappingURL=index.js.map